import { Alert, Button, Flex, Input, Tag, Typography } from 'antd'
import { TooltipParagraph } from 'features/drilldown/components/TooltipParagraph'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useOntology } from 'features/dynamicOntology'
import { ChangeEvent, FC, ReactNode, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { TermMatchRow } from './TermMatchRow'
import { useValueListFilter } from './useValueListFilter'
import { normalizeTerm } from './valueListFilterMatching'
import { ValueListFilterPanelData } from './valueListFilterPanelData'

const { Text } = Typography
const { TextArea } = Input

const EMPTY_VALUES: ReadonlyArray<string> = []

const PASTE_AUTO_SIZE = { minRows: 3, maxRows: 10 }

export const VALUE_LIST_FILTER_TITLE_TOOLTIP: ReactNode = (
  <TooltipParagraph>
    Paste a column of values from Excel (newline or comma separated) to scope
    the panels <b>below</b> this one to the entries that match. Choose which
    field to match on in the panel configuration.
  </TooltipParagraph>
)

const ReviewList = styled(Flex)`
  max-height: 18rem;
  overflow-y: auto;
`

export const ValueListFilterPanel: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<ValueListFilterPanelData>()
  const { pastedText, chosenValuesByTerm, shouldUppercaseValues } = panelData

  const {
    report,
    fieldValueCount,
    isLoadingFieldValues,
    matchFieldDef,
    secondaryValuesByValue,
  } = useValueListFilter()
  const { entryTypeNames } = useOntology()

  const matchFieldLabel = matchFieldDef
    ? `${entryTypeNames[matchFieldDef.entryType] ?? matchFieldDef.entryType} → ${matchFieldDef.name}`
    : 'no field selected'

  const handlePastedTextChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setPanelData((prev) => ({ ...prev, pastedText: event.target.value }))
    },
    [setPanelData]
  )

  const handleChooseValues = useCallback(
    (term: string, values: ReadonlyArray<string>) => {
      setPanelData((prev) => ({
        ...prev,
        chosenValuesByTerm: {
          ...prev.chosenValuesByTerm,
          [normalizeTerm(term)]: values,
        },
      }))
    },
    [setPanelData]
  )

  const handleClear = useCallback(() => {
    setPanelData((prev) => ({
      ...prev,
      pastedText: '',
      chosenValuesByTerm: {},
    }))
  }, [setPanelData])

  const reviewRows = useMemo(
    () =>
      report.matches
        .map((match, index) => ({ match, line: index + 1 }))
        .filter(({ match }) => match.status !== 'matched')
        .map(({ match, line }) => ({
          key: `${line}-${match.term}`,
          term: match.term,
          status: match.status,
          tier: match.tier,
          candidateValues: match.candidateValues,
          chosenValues:
            chosenValuesByTerm[normalizeTerm(match.term)] ?? EMPTY_VALUES,
          duplicateOfLine:
            match.duplicateOfTermIndex === undefined
              ? undefined
              : match.duplicateOfTermIndex + 1,
        })),
    [chosenValuesByTerm, report.matches]
  )

  const { counts, matches } = report
  const hasTerms = matches.length > 0

  return (
    <Flex vertical gap='small'>
      <Flex align='center' justify='space-between' gap='small'>
        <Text type='secondary'>
          Matching {matchFieldLabel}
          {isLoadingFieldValues
            ? ' · loading values…'
            : ` · ${fieldValueCount} values in scope`}
        </Text>
        {hasTerms && (
          <Button size='small' type='link' onClick={handleClear}>
            Clear
          </Button>
        )}
      </Flex>

      <TextArea
        value={pastedText}
        onChange={handlePastedTextChange}
        autoSize={PASTE_AUTO_SIZE}
        placeholder='Paste a column of values from Excel, or a comma-separated list'
      />

      {!matchFieldDef && (
        <Alert
          type='warning'
          showIcon
          message='Choose a field to match on in the panel configuration'
        />
      )}

      {hasTerms && (
        <Flex align='center' gap='small' wrap>
          <Text>
            <b>{counts.matched}</b> of {matches.length} terms matched
          </Text>
          {counts.ambiguous > 0 && (
            <Tag color='warning'>{counts.ambiguous} need a choice</Tag>
          )}
          {counts.duplicate > 0 && (
            <Tag color='gold'>{counts.duplicate} duplicate</Tag>
          )}
          {counts.unmatched > 0 && <Tag>{counts.unmatched} not found</Tag>}
        </Flex>
      )}

      {hasTerms && !counts.matched && !isLoadingFieldValues && (
        <Alert
          type='info'
          showIcon
          message={`Nothing matched ${matchFieldLabel} — try a different field in the panel configuration`}
        />
      )}

      {reviewRows.length > 0 && (
        <ReviewList vertical gap='small'>
          {reviewRows.map(({ key, ...rowProps }) => (
            <TermMatchRow
              key={key}
              {...rowProps}
              secondaryValuesByValue={secondaryValuesByValue}
              shouldUppercaseValues={shouldUppercaseValues}
              onChooseValues={handleChooseValues}
            />
          ))}
        </ReviewList>
      )}
    </Flex>
  )
}
